/**
 * LLM-backed description advisor (Spec 6).
 *
 * Implements the swappable `DescriptionAdvisor` seam (mirroring
 * `createLlmParameterAdvisor`), but calls the real Description Generation API
 * (Spec 6, `POST /api/onboarding/generate-description-async`) instead of a
 * client-side template. Built as a factory so the call site can bind the extra
 * fields the backend needs (toolName/category/confirmed metrics/
 * supportsPeriodComparison) that aren't part of `DescriptionSuggestionInput`.
 *
 * Reuses `buildBusinessContext` from `llmMetricAdvisor.ts`. Unlike the metric/
 * parameter advisors it does NOT send a columns payload (the description request
 * has none); it maps the input's full ParameterDraft[] down to `parameterNames`
 * for the wire request.
 *
 * A network-free `heuristicDescriptionAdvisor` fallback is exported for local/
 * dev where no backend endpoint is configured (BACKEND_AVAILABLE === false).
 */

import {
  DescriptionAdvisor,
  DescriptionSuggestion,
  DescriptionSuggestionInput,
  GenerateDescriptionRequest,
  GenerateDescriptionResponse,
} from './types';
import { submitGenerateDescription } from './onboardingApi';
import { pollInferJob } from './pollInferJob';
import { buildBusinessContext } from './llmMetricAdvisor';

/** Dependencies the LLM description advisor needs beyond DescriptionSuggestionInput. */
export interface LlmDescriptionAdvisorDeps {
  toolName: string;
  category: string;
  supportsPeriodComparison: boolean;
  /**
   * Injectable end-to-end generation fn (submit → poll → response). Defaults to
   * the composed submitGenerateDescription + pollInferJob. Tests inject this to
   * return a GenerateDescriptionResponse directly, bypassing the async transport.
   */
  generateDescriptionFn?: (req: GenerateDescriptionRequest) => Promise<GenerateDescriptionResponse>;
  /** Injectable submit client (Spec 3.5/6 async); defaults to submitGenerateDescription. */
  submitFn?: typeof submitGenerateDescription;
  /** Injectable poll loop (Spec 3.5 async); defaults to pollInferJob. */
  pollFn?: typeof pollInferJob;
}

/** Maps the LLM response to a DescriptionSuggestion. */
function toSuggestion(response: GenerateDescriptionResponse): DescriptionSuggestion {
  return {
    description: response.description,
    sections: response.sectionsIncluded,
    warnings: response.warnings,
  };
}

/**
 * Builds a DescriptionAdvisor that generates the tool description via the Spec 6
 * LLM endpoint. Bind it at the call site with the tool/metric context, then pass
 * it into DescriptionStep's `advisor` prop.
 */
export function createLlmDescriptionAdvisor(deps: LlmDescriptionAdvisorDeps): DescriptionAdvisor {
  const submit = deps.submitFn ?? submitGenerateDescription;
  const poll = deps.pollFn ?? pollInferJob;
  // Default: submit an async job, poll until COMPLETED, cast the free-form
  // result to the description response shape. Tests may inject
  // `generateDescriptionFn` to short-circuit this transport while keeping the
  // request/response mapping.
  const callGenerate =
    deps.generateDescriptionFn ??
    (async (req: GenerateDescriptionRequest): Promise<GenerateDescriptionResponse> => {
      const { jobId } = await submit(req);
      const result = await poll(jobId);
      return result as GenerateDescriptionResponse;
    });
  return {
    async suggest(input: DescriptionSuggestionInput): Promise<DescriptionSuggestion> {
      const response = await callGenerate({
        toolName: deps.toolName,
        category: deps.category,
        confirmedMetrics: {
          baseMetrics: input.confirmedBaseMetrics,
          derivedMetrics: input.confirmedDerivedMetrics,
        },
        parameterNames: input.parameters.map((p) => p.parameterName),
        businessContext: buildBusinessContext(input.businessContext, input.columnNotes),
        supportsPeriodComparison: deps.supportsPeriodComparison,
      });
      return toSuggestion(response);
    },
  };
}

/**
 * Heuristic description advisor (client-side, network-free). The fallback when
 * no backend endpoint is configured (local/dev). Emits a minimal skeleton with
 * the two required sections (PRIMARY PURPOSE, SCOPE) so the user has a starting
 * point to edit; carries a warning that it is a placeholder.
 */
export const heuristicDescriptionAdvisor: DescriptionAdvisor = {
  async suggest(input: DescriptionSuggestionInput): Promise<DescriptionSuggestion> {
    const purpose =
      input.businessContext.trim().length > 0
        ? input.businessContext.trim()
        : 'Describe what this tool reports and the questions it can answer.';
    const metricList = input.confirmedBaseMetrics
      .map((m) => `- ${m.metricKey}`)
      .concat(input.confirmedDerivedMetrics.map((m) => `- ${m.derivedMetricKey}`));
    const paramList = input.parameters.map((p) => `- ${p.parameterName}`);

    const lines = [
      'PRIMARY PURPOSE',
      purpose,
      '',
      'SCOPE',
      metricList.length > 0
        ? `Reports the following metrics:\n${metricList.join('\n')}`
        : 'Reports metrics defined on the Metrics step.',
    ];
    if (paramList.length > 0) {
      lines.push('', 'PARAMETERS', paramList.join('\n'));
    }

    return {
      description: lines.join('\n'),
      sections: paramList.length > 0
        ? ['PRIMARY PURPOSE', 'SCOPE', 'PARAMETERS']
        : ['PRIMARY PURPOSE', 'SCOPE'],
      warnings: [
        'This is a local placeholder description (no backend configured). Edit it or generate with AI in alpha.',
      ],
    };
  },
};
